import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BookOpenText,
  UploadSimple,
  Trash,
  Eye,
  FilePdf,
  FileDoc,
  FileXls,
  FileText,
  Sparkle,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import {
  listKnowledge,
  uploadKnowledge,
  deleteKnowledge,
  getKnowledge,
  formatApiError,
} from "../lib/api";
import { formatDate } from "../lib/format";

const ICONS = { ".pdf": FilePdf, ".docx": FileDoc, ".xlsx": FileXls, ".xls": FileXls };
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

export default function KnowledgeAIPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      setDocs(await listKnowledge());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const handleUpload = async () => {
    if (!files.length) return toast.error("Pilih file terlebih dahulu.");
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ name: f.name, pct: 0, idx: i + 1, total: files.length });
      try {
        const doc = await uploadKnowledge(f, description, (pct) =>
          setProgress({ name: f.name, pct, idx: i + 1, total: files.length }),
        );
        toast.success(`"${doc.filename}" ditambahkan (${doc.chunk_count} potongan teks).`);
      } catch (e) {
        toast.error(`${f.name}: ${formatApiError(e)}`);
      }
    }
    setProgress(null);
    setFiles([]);
    setDescription("");
    if (inputRef.current) inputRef.current.value = "";
    refresh();
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Hapus dokumen "${d.filename}" dari Knowledge AI?`)) return;
    try {
      await deleteKnowledge(d.id);
      toast.success("Dokumen dihapus.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handlePreview = async (d) => {
    try {
      setPreview(await getKnowledge(d.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalChunks = docs.reduce((s, d) => s + (d.chunk_count || 0), 0);

  return (
    <div className="flex flex-col gap-6" data-testid="knowledge-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Pusat Pengetahuan
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <BookOpenText size={28} weight="duotone" className="text-[#0A3F9A]" /> Knowledge AI
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Unggah buku tarif, SK layanan baru, dan dokumen referensi lain. Isinya dipakai Mode AI
            sebagai dasar rekomendasi penyusunan tarif biaya operasi.
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded shadow-sm px-5 py-3 text-sm flex gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Dokumen</div>
            <div className="text-2xl font-black text-slate-900" data-testid="knowledge-doc-count">
              {docs.length}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Potongan Teks</div>
            <div className="text-2xl font-black text-slate-900">{totalChunks}</div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded shadow-sm p-5 md:p-6" data-testid="knowledge-upload-card">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-3 flex items-center gap-1.5">
            <UploadSimple size={14} weight="bold" /> Unggah Dokumen (Admin)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-600">File (PDF, DOCX, XLSX, TXT) — bisa lebih dari satu</label>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.xls,.txt,.md,.csv"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="text-sm file:mr-3 file:bg-[#0A3F9A] file:text-white file:border-0 file:px-3 file:py-2 file:rounded file:text-xs file:font-bold border border-slate-300 rounded w-full"
                data-testid="knowledge-file-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-600">Keterangan (opsional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Mis. SK Direktur No. 012/2026 tentang tarif layanan baru"
                className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="knowledge-description-input"
              />
            </div>
            <button
              onClick={handleUpload}
              disabled={!!progress || !files.length}
              className="bg-[#0A3F9A] text-white px-5 py-2.5 rounded hover:bg-[#082F73] text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="knowledge-upload-btn"
            >
              <UploadSimple size={16} weight="bold" />
              {progress ? "Mengunggah..." : `Unggah${files.length > 1 ? ` (${files.length})` : ""}`}
            </button>
          </div>
          {progress && (
            <div className="mt-4" data-testid="knowledge-upload-progress">
              <div className="flex justify-between text-xs text-slate-600 mb-1">
                <span>
                  {progress.idx}/{progress.total} · {progress.name}
                </span>
                <span>{progress.pct}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded overflow-hidden">
                <div
                  className="h-full bg-[#0A3F9A] transition-[width] duration-300"
                  style={{ width: `${progress.pct}%` }}
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                File dikirim bertahap (2 MB per bagian) lalu teksnya diekstrak di server.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-slate-500">Memuat...</div>
        ) : docs.length === 0 ? (
          <div className="p-10 text-center" data-testid="knowledge-empty">
            <Sparkle size={36} weight="duotone" className="mx-auto text-slate-300" />
            <div className="mt-2 text-sm text-slate-600">
              Belum ada dokumen. {isAdmin ? "Unggah buku tarif atau SK untuk memulai." : "Hubungi Admin untuk menambahkan dokumen."}
            </div>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse" data-testid="knowledge-table">
            <thead>
              <tr>
                {["Dokumen", "Keterangan", "Ukuran", "Potongan", "Diunggah", "Aksi"].map((h, i) => (
                  <th
                    key={h}
                    className={`bg-slate-50 text-slate-600 font-semibold p-3 border-b ${i >= 5 ? "text-right" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const Icon = ICONS[d.ext] || FileText;
                return (
                  <tr key={d.id} className="hover:bg-slate-50" data-testid={`knowledge-row-${d.id}`}>
                    <td className="p-3 border-b border-slate-100">
                      <div className="flex items-center gap-2 font-medium text-slate-900">
                        <Icon size={18} weight="duotone" className="text-[#0A3F9A] shrink-0" />
                        <span className="break-all">{d.filename}</span>
                      </div>
                    </td>
                    <td className="p-3 border-b border-slate-100 text-slate-600 text-xs max-w-[260px]">
                      {d.description || "-"}
                    </td>
                    <td className="p-3 border-b border-slate-100 text-slate-600 text-xs whitespace-nowrap">
                      {fmtSize(d.size_bytes || 0)}
                    </td>
                    <td className="p-3 border-b border-slate-100 text-slate-600 text-xs">{d.chunk_count}</td>
                    <td className="p-3 border-b border-slate-100 text-slate-600 text-xs whitespace-nowrap">
                      {formatDate(d.created_at)}
                      <div className="text-slate-400">{d.uploaded_by}</div>
                    </td>
                    <td className="p-3 border-b border-slate-100 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handlePreview(d)}
                          className="text-slate-600 hover:text-[#0A3F9A] p-1"
                          title="Lihat isi teks"
                          data-testid={`knowledge-preview-${d.id}`}
                        >
                          <Eye size={18} weight="duotone" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(d)}
                            className="text-slate-600 hover:text-red-600 p-1"
                            title="Hapus"
                            data-testid={`knowledge-delete-${d.id}`}
                          >
                            <Trash size={18} weight="duotone" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="knowledge-preview-modal"
        >
          <div
            className="bg-white rounded shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Isi Dokumen</div>
                <div className="font-semibold text-slate-900 break-all">{preview.filename}</div>
                <div className="text-xs text-slate-500">
                  {preview.char_count?.toLocaleString("id-ID")} karakter · {preview.chunk_count} potongan
                </div>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="text-sm text-slate-600 hover:text-slate-900 px-2 py-1"
                data-testid="knowledge-preview-close"
              >
                Tutup
              </button>
            </div>
            <pre className="p-5 text-xs text-slate-700 whitespace-pre-wrap overflow-auto font-mono leading-relaxed">
              {preview.text}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
